import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Globe2, Coins } from "lucide-react";
import { toast } from "sonner";

/**
 * PreferencesCard — Settings › Account
 *
 * Two-column card exposing:
 *   1. Preferred language (6 options — EN / SW / SH / FR / AR / PT)
 *   2. Preferred display currency (9 options — KES/USD/EUR/GBP/TZS/UGX/RWF/ETB/ZAR)
 *
 * Changes are persisted via PATCH /api/preferences and refresh the auth
 * context so downstream widgets (dashboard portfolio card, PnL rows) can
 * re-render in the new currency.
 */
export default function PreferencesCard() {
    const { user, refresh } = useAuth();
    const [opts, setOpts] = useState({ languages: [], currencies: [] });
    const [lang, setLang] = useState(user?.preferred_language || "en");
    const [ccy, setCcy] = useState(user?.preferred_display_currency || "KES");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/preferences/options")
            .then(r => setOpts(r.data || { languages: [], currencies: [] }))
            .catch(() => {});
    }, []);

    useEffect(() => {
        setLang(user?.preferred_language || "en");
        setCcy(user?.preferred_display_currency || "KES");
    }, [user]);

    async function save(patch, successMsg) {
        setBusy(true);
        try {
            await api.patch("/preferences", patch);
            await refresh();
            toast.success(successMsg);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to update preference");
        } finally { setBusy(false); }
    }

    return (
        <div
            className="border border-border bg-card rounded-xl p-4"
            data-testid="preferences-card"
        >
            <div className="overline mb-3">Language &amp; currency</div>
            <p className="text-xs text-muted-foreground mb-4">
                Choose how Melian Dialogue talks to you. Obondo, Kimani, and every
                onboarding surface adapt to the language you pick. Portfolio balances
                and PnL render in the display currency you select — the underlying
                ledger stays in KES.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Language */}
                <label className="block">
                    <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground mb-2">
                        <Globe2 className="w-3.5 h-3.5" /> Preferred language
                    </div>
                    <select
                        value={lang}
                        disabled={busy || !opts.languages.length}
                        onChange={(e) => {
                            const v = e.target.value;
                            setLang(v);
                            save({ preferred_language: v }, `Language set to ${e.target.selectedOptions[0].textContent}`);
                        }}
                        className="w-full bg-background border border-border rounded px-3 py-2 text-sm"
                        data-testid="preferences-language-select"
                    >
                        {opts.languages.map(l => (
                            <option key={l.code} value={l.code}>{l.label}</option>
                        ))}
                    </select>
                    <p className="text-[11px] text-muted-foreground mt-1.5">
                        AI assistants respond in this language. Interface strings roll
                        out language-by-language during the multilingual push.
                    </p>
                </label>

                {/* Display currency */}
                <label className="block">
                    <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground mb-2">
                        <Coins className="w-3.5 h-3.5" /> Display currency
                    </div>
                    <select
                        value={ccy}
                        disabled={busy || !opts.currencies.length}
                        onChange={(e) => {
                            const v = e.target.value;
                            setCcy(v);
                            save({ preferred_display_currency: v }, `Display currency set to ${v}`);
                        }}
                        className="w-full bg-background border border-border rounded px-3 py-2 text-sm"
                        data-testid="preferences-currency-select"
                    >
                        {opts.currencies.map(c => (
                            <option key={c.code} value={c.code}>{c.symbol} · {c.code} — {c.label}</option>
                        ))}
                    </select>
                    <p className="text-[11px] text-muted-foreground mt-1.5">
                        FX rates refresh every 6 hours from open.er-api.com. Trades
                        are always executed and cleared in KES.
                    </p>
                </label>
            </div>
        </div>
    );
}
